package com.mencanhealth.admin.data

import com.mencanhealth.admin.model.AdminUser
import com.mencanhealth.admin.model.DashboardStats
import com.mencanhealth.admin.model.DeletionRequest
import com.mencanhealth.admin.model.SubscriptionTier
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore

class AdminFirestoreService {

    private val firestore = Firebase.firestore

    suspend fun listUsers(): List<AdminUser> {
        val snapshot = firestore.collection("users").get()
        return snapshot.documents.map { doc ->
            val tierStr = try { doc.get<String>("tier") } catch (_: Exception) { "FREE" }
            val tier = try { SubscriptionTier.valueOf(tierStr) } catch (_: Exception) { SubscriptionTier.FREE }
            AdminUser(
                uid = doc.id,
                email = try { doc.get<String>("email") } catch (_: Exception) { "" },
                tier = tier,
                displayName = try { doc.get<String>("displayName") } catch (_: Exception) { null },
                lastSync = try { doc.get<Long>("lastSync") } catch (_: Exception) { null }
            )
        }
    }

    suspend fun getUser(uid: String): AdminUser? {
        return try {
            val doc = firestore.collection("users").document(uid).get()
            if (!doc.exists) return null
            val tierStr = try { doc.get<String>("tier") } catch (_: Exception) { "FREE" }
            val tier = try { SubscriptionTier.valueOf(tierStr) } catch (_: Exception) { SubscriptionTier.FREE }
            AdminUser(
                uid = doc.id,
                email = try { doc.get<String>("email") } catch (_: Exception) { "" },
                tier = tier,
                displayName = try { doc.get<String>("displayName") } catch (_: Exception) { null },
                lastSync = try { doc.get<Long>("lastSync") } catch (_: Exception) { null }
            )
        } catch (_: Exception) { null }
    }

    suspend fun updateTier(uid: String, newTier: SubscriptionTier) {
        firestore.collection("users").document(uid).update("tier" to newTier.name)
    }

    suspend fun listDeletionRequests(): List<DeletionRequest> {
        val snapshot = firestore.collection("deletion_requests")
            .orderBy("requestedAt", dev.gitlive.firebase.firestore.Direction.DESCENDING)
            .get()
        return snapshot.documents.map { doc ->
            DeletionRequest(
                id = doc.id,
                email = try { doc.get<String>("email") } catch (_: Exception) { "" },
                reason = try { doc.get<String>("reason") } catch (_: Exception) { "" },
                requestedAt = try { doc.get<Long>("requestedAt") } catch (_: Exception) { 0L },
                status = try { doc.get<String>("status") } catch (_: Exception) { "pending" },
                completedAt = try { doc.get<Long>("completedAt") } catch (_: Exception) { null }
            )
        }
    }

    suspend fun executeDeletion(request: DeletionRequest) {
        // Find user by email
        val usersSnapshot = firestore.collection("users")
            .where { "email" equalTo request.email }
            .get()

        for (userDoc in usersSnapshot.documents) {
            val uid = userDoc.id

            // Delete chat messages subcollection
            try {
                val messagesSnapshot = firestore.collection("chats").document(uid)
                    .collection("messages").get()
                for (msgDoc in messagesSnapshot.documents) {
                    firestore.collection("chats").document(uid)
                        .collection("messages").document(msgDoc.id).delete()
                }
            } catch (_: Exception) {}

            // Delete chat doc
            try {
                firestore.collection("chats").document(uid).delete()
            } catch (_: Exception) {}

            // Delete user doc
            firestore.collection("users").document(uid).delete()
        }

        // Mark request as completed
        val now = kotlin.js.Date().getTime().toLong()
        firestore.collection("deletion_requests").document(request.id)
            .update(
                "status" to "completed",
                "completedAt" to now
            )
    }

    suspend fun rejectDeletion(requestId: String) {
        firestore.collection("deletion_requests").document(requestId)
            .update("status" to "rejected")
    }

    suspend fun getStats(): DashboardStats {
        val users = listUsers()
        return DashboardStats(
            totalUsers = users.size,
            freeUsers = users.count { it.tier == SubscriptionTier.FREE },
            proUsers = users.count { it.tier == SubscriptionTier.PRO },
            premiumUsers = users.count { it.tier == SubscriptionTier.PREMIUM },
            adminUsers = users.count { it.tier == SubscriptionTier.ADMIN }
        )
    }
}
